import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowUpRight } from "lucide-react";
import devCoreLogo from "@/assets/devcore-logo.webp";
import { scrollToSection } from "@/lib/scrollToSection";
import { useLocale } from "@/i18n/LocaleProvider";
import { parsePath } from "@/i18n/locales";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { subscribeScroll } from "@/lib/scrollEngine";

type NavItem = { key: string; label: string; hash?: string; href?: string };

// Nav labels are intentionally kept in English across all locales.
const navItems: NavItem[] = [
  { key: "nav.home", label: "Home", hash: "#home" },
  { key: "nav.services", label: "Build", hash: "#services" },
  { key: "nav.work", label: "Work", hash: "#work" },
  { key: "nav.blueprint", label: "Blueprint", hash: "#blueprint" },
  { key: "nav.blog", label: "Blog", href: "/blog" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { localize } = useLocale();
  const isHome = parsePath(location.pathname).path === "/";

  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    if (isHome) {
      scrollToSection(hash.replace("#", ""));
    } else {
      navigate(localize("/" + hash));
    }
    setOpen(false);
  };

  const handleBuildClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(localize("/contact"));
    setOpen(false);
  };

  useEffect(() => {
    return subscribeScroll(() => setScrolled(window.scrollY > 20));
  }, []);

  // Handle hash scroll on page load (when navigating from subpage)
  useEffect(() => {
    if (isHome && location.hash) {
      setTimeout(() => {
        scrollToSection(location.hash.replace("#", ""));
      }, 100);
    }
  }, [isHome, location.hash]);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-[80] isolate transition-[height,background-color,border-color,box-shadow] duration-500 ${
        open
          ? "h-[100dvh] overflow-hidden bg-background border-b border-border"
          : scrolled || !isHome
            ? "bg-background/90 backdrop-blur-xl border-b border-border/50 shadow-lg shadow-primary/5"
            : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container relative z-20 flex items-center justify-between h-16 lg:h-20">

        <a
          href={localize(isHome ? "/#home" : "/")}
          onClick={(e) => handleHashClick(e, "#home")}
          className="relative z-10 flex items-center"
        >
          <img
            src={devCoreLogo}
            alt="DevCore Websystems"
            className="h-8 lg:h-10 w-auto"
          />
        </a>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-8 relative z-10">
          {navItems.map((link) =>
            link.href ? (
              <a
                key={link.key}
                href={localize(link.href)}
                onClick={(e) => { e.preventDefault(); navigate(localize(link.href!)); }}
                className={`text-sm font-medium transition-colors duration-[var(--duration-state)] relative group ${
                  scrolled || open || !isHome ? "text-muted-foreground hover:text-foreground" : "text-primary-foreground/70 hover:text-primary-foreground"
                }`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-full h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </a>
            ) : (
              <a
                key={link.key}
                href={localize(isHome ? link.hash! : "/" + link.hash!)}
                onClick={(e) => handleHashClick(e, link.hash!)}
                className={`text-sm font-medium transition-colors duration-[var(--duration-state)] relative group ${
                  scrolled || open || !isHome ? "text-muted-foreground hover:text-foreground" : "text-primary-foreground/70 hover:text-primary-foreground"
                }`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-full h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </a>
            )
          )}
          <LanguageSwitcher />
          <span className="hover-glow-accent">
            <a
              href={localize("/contact")}
              onClick={handleBuildClick}
              className="inline-flex items-center justify-center px-5 py-2.5 btn-clip bg-primary text-primary-foreground btn-label-contrast text-sm font-medium transition-transform duration-[var(--duration-state)] active:scale-[0.97] relative overflow-hidden group"
            >
              <span className="relative z-10">{t("common.startBuild")}</span>
              <span className="absolute inset-0 bg-accent/20 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500" />
            </a>
          </span>
        </div>
        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
         className={`lg:hidden relative z-30 p-2 active:scale-95 transition-[color,transform] duration-200 ${
             scrolled || open || !isHome ? "text-foreground" : "text-primary-foreground"
           }`}
          aria-label="Toggle menu"
        >
          <div className="relative w-[22px] h-[22px]">
            <X
              size={22}
              className={`absolute inset-0 transition-[transform,opacity] duration-300 ${
                open ? "rotate-0 opacity-100" : "rotate-90 opacity-0"
              }`}
            />
            <Menu
              size={22}
              className={`absolute inset-0 transition-[transform,opacity] duration-300 ${
                open ? "-rotate-90 opacity-0" : "rotate-0 opacity-100"
              }`}
            />
          </div>
        </button>
      </div>

      {/* Mobile full-screen menu */}
      <div
        className={`lg:hidden absolute left-0 right-0 top-16 z-10 h-[calc(100dvh-4rem)] transition-opacity duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* layered tech backdrop — solid, fully obscures page content */}
        <div className="absolute inset-0 bg-background" />
        <div className="absolute inset-0 bg-card/95" />
        <div className="absolute inset-0 tech-grid opacity-[0.12] pointer-events-none" />
        <div className="absolute inset-0 tech-dots opacity-[0.5] pointer-events-none" />
        {/* animated scan line */}
        <div
          className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent transition-[top,opacity] duration-700 ease-out"
          style={{ top: open ? "100%" : "0%", opacity: open ? 0 : 0.8 }}
        />

        <div className="relative z-10 h-full flex flex-col container pt-8 pb-10">
          <div
            className="mb-8 flex items-center justify-between transition-[opacity,transform] duration-500"
            style={{
              transitionDelay: open ? "120ms" : "0ms",
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0)" : "translateY(-10px)",
            }}
          >
            <p className="mono-label text-accent">
              // {t("nav.label")}
            </p>
            <LanguageSwitcher />
          </div>

          <div className="flex flex-col">
            {navItems.map((link, i) => (
              <a
                key={link.key}
                href={link.href ? localize(link.href) : localize(isHome ? link.hash! : "/" + link.hash!)}
                onClick={(e) => {
                  e.preventDefault();
                  if (link.href) {
                    navigate(localize(link.href));
                  } else {
                    handleHashClick(e, link.hash!);
                  }
                  setOpen(false);
                }}
                className="group relative flex items-baseline gap-4 border-b border-border/40 py-5 overflow-hidden"
                style={{
                  transitionProperty: "opacity, transform",
                  transitionDuration: "600ms",
                  transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)",
                  transitionDelay: open ? `${i * 80 + 180}ms` : "0ms",
                  opacity: open ? 1 : 0,
                  transform: open ? "translateY(0)" : "translateY(24px)",
                }}
              >
                {/* hover sweep */}
                <span className="absolute inset-0 bg-gradient-to-r from-accent/0 via-accent/[0.07] to-accent/0 -translate-x-full group-hover:translate-x-full group-active:translate-x-full transition-transform duration-700 pointer-events-none" />
                <span className="font-mono text-xs text-accent/70 tabular-nums shrink-0 w-8">
                  0{i + 1}
                </span>
                <span className="font-heading text-3xl font-bold tracking-tight text-foreground transition-colors duration-300 group-hover:text-accent">
                  {link.label}
                </span>
                <ArrowUpRight
                  size={22}
                  className="ml-auto self-center text-muted-foreground/40 transition-[color,transform] duration-300 group-hover:text-accent group-hover:translate-x-1 group-hover:-translate-y-1"
                />
                {/* underline accent */}
                <span className="absolute bottom-0 left-0 h-px w-full bg-accent scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300" />
              </a>
            ))}
          </div>

          <div className="mt-auto">
            <span className="hover-glow-accent block">
              <a
                href={localize("/contact")}
                onClick={handleBuildClick}
                className="group relative inline-flex w-full items-center justify-center gap-3 px-6 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast text-sm font-medium overflow-hidden transition-[opacity,transform] duration-500 active:scale-[0.98]"
                style={{
                  transitionDelay: open ? `${navItems.length * 80 + 240}ms` : "0ms",
                  opacity: open ? 1 : 0,
                  transform: open ? "translateY(0)" : "translateY(16px)",
                }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-foreground/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative z-10">{t("common.startBuild")}</span>
                <ArrowUpRight size={18} className="relative z-10 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </span>
            <div
              className="flex items-center gap-5 font-mono text-[11px] text-muted-foreground/60 mt-6 transition-opacity duration-500"
              style={{
                transitionDelay: open ? `${navItems.length * 80 + 320}ms` : "0ms",
                opacity: open ? 1 : 0,
              }}
            >
              <a
                href={localize("/imprint")}
                onClick={(e) => { e.preventDefault(); navigate(localize("/imprint")); setOpen(false); }}
                className="hover:text-accent transition-colors"
              >
                {t("footer.imprint")}
              </a>
              <a
                href={localize("/privacy-policy")}
                onClick={(e) => { e.preventDefault(); navigate(localize("/privacy-policy")); setOpen(false); }}
                className="hover:text-accent transition-colors"
              >
                {t("footer.privacy")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
